import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import CurvedArrow from '@/assets/curved-next-arrow.png';
import { Button } from '@/components/Elements';
import { BillingModeUnique } from '@/components/Elements/BillingModeUnique';
import { CurrencySwitch } from '@/components/Elements/CurrencySwitch';
import { ProductCard } from '@/components/Elements/ProductCard';
import { Slider, SliderProps } from '@/components/Elements/Slider';
import { LANG_DE, LANG_ES, UNKNOWN_DATA } from '@/config/appConstants';
import { Country } from '@/features/countries';
import { SmsCredits } from '@/features/smsCredits';
import { useMe } from '@/hooks/useMe';
import { useUserProducts } from '@/hooks/useUserProducts';
import {
  BillingMode,
  Currency,
  Me,
  ParsedSliderMarkType,
  ProductFeaturesTypes,
  ProductTypes,
  SliderMarkType,
  UserProductTypes,
} from '@/types';
import { renderSelect } from '@/utils/forms';
import { getNoPlanProduct, shouldShowTheCurrencySwitch } from '@/utils/helpers';

const SMSBodyWrap = styled.div`
  .sms__body__description::after {
    content: none;
  }

  .sms__body__description__advise {
    border: 3px solid #e7ab39;
  }

  .sms__body__description__advise__button {
    border: 2px solid #e7ab39;
    background-color: rgba(255, 255, 255, 0.5);
    text-transform: none;
  }

  @media (min-width: 1024px) {
    .sms__body__description::after {
      background-image: url(${CurvedArrow});
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
      content: ' ';
      display: block;
      height: 128px;
      margin-left: auto;
      position: absolute;
      right: -107px;
      top: 35px;
      width: 128px;
      z-index: 9;
      transform: rotate(25deg);
    }
  }
`;

export type SMSMarketingContainerProps = {
  title: string;
  products: ProductTypes[][];
  billingMode?: BillingMode;
  defaultSMSCountryProvider?: string;
  onProductSelect: (product: ProductTypes) => void;
  onRequestInfo: (product: ProductTypes, origen?: string) => void;
  userProducts?: UserProductTypes[];
  countries: Country[];
  smsCredits: SmsCredits;
  userIsLogged?: boolean;
} & SliderProps;

export const SMSMarketingContainer = ({
  title,
  products,
  min = 0,
  max,
  onSliderChange,
  onProductSelect,
  onRequestInfo,
  defaultValue = 0,
  marks = {},
  defaultSMSCountryProvider = LANG_ES,
  billingMode = 'unique',
  countries,
  smsCredits,
  userProducts,
  userIsLogged,
}: SMSMarketingContainerProps) => {
  const { getMe } = useMe();
  const me = getMe();
  const step = 1;
  const { t, i18n } = useTranslation();

  const [productsCollection, setProductsCollection] = useState(getNoPlanProduct(products, marks));
  const [currentStep, setCurrentStep] = useState(0);
  const { setSelectedProductCurrency, getSelectedUserProduct } = useUserProducts();
  const { selectedProductCurrency } = getSelectedUserProduct();
  const [selectedCountryProvider, setSelectedCountryProvider] = useState<string>(
    defaultSMSCountryProvider || LANG_ES
  );

  const formatNumberByLanguage = (num: number) => {
    /*
     * If user has spanish language, we have to change it on the fly for a more friendly number format:
     * On the ISO, spanish numbers have a 'Minimum Grouping Digits' of 2, so 1234 is formatted as 1234, while 12345 is formatted as 12.345.
     * On deutsch, the 'Minimum Grouping Digits' is 1, so 1234 is formatted as 1.234
     **/
    const formatter = i18n.language === LANG_ES ? LANG_DE : i18n.language;
    return new Intl.NumberFormat(formatter, {}).format(num);
  };

  const parseMarks = (marks: SliderMarkType) => {
    const keys = Object.keys(marks);
    const result: ParsedSliderMarkType = {};

    keys.forEach((key: string) => {
      const iKey = +key;
      result[iKey] = (
        <div className="min-w-[100px]">
          <span className="hidden lg:block font-bold block text-black">
            {iKey === keys.length - 1 ? '>' : ''}
            {t(`productSteps.${marks[iKey]}`)}
          </span>
          <span className="hidden lg:block block text-black">
            {t('productTypes.smsMarketing.credits')}
          </span>
        </div>
      );
    });

    return result;
  };

  const marksParsed = parseMarks(marks);

  const unselectStep = () => {
    const currentSelectedStepLabel = document.querySelector('.step--selected');
    currentSelectedStepLabel && currentSelectedStepLabel.classList.remove('step--selected');
  };

  const selectStep = (step: number = currentStep + 1) => {
    unselectStep();

    const currentStepLabel = document.querySelector(
      `.rc-slider-mark-text:nth-of-type(${step}) > div`
    );

    currentStepLabel && currentStepLabel.classList.add('step--selected');
  };

  useEffect(() => {
    selectStep();
  });

  const onDefaultSliderChange = (value: number) => {
    setCurrentStep(value);
    setProductsCollection(getNoPlanProduct(products, marks, value));

    selectStep(value);

    onSliderChange && onSliderChange(value);
  };

  const onChangeCurrencySwitch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedProductCurrency(event.target.value as Currency);

  const renderLabel = (language: string, item: Country) => {
    language = language.toUpperCase();
    return (item.name as any)[language];
  };

  const formatSMSProductName = (name: string | ProductFeaturesTypes) =>
    t('productTypes.smsMarketing.' + name);

  const getSMSCountryName = () => {
    if (selectedCountryProvider) {
      return selectedCountryProvider.toUpperCase();
    }

    return LANG_ES.toUpperCase();
  };

  const formatCredits = (product: ProductTypes) => {
    const country = getSMSCountryName() as keyof typeof smsCredits;
    const credits: number = +(smsCredits[country] || smsCredits['--']) as number;
    const quantity: number = +product.info.productStep || 0;

    const totalCredits = !credits ? UNKNOWN_DATA : Math.round(quantity / credits);

    return (
      <div className="flex items-center justify-between p-4 mt-5 bg-md-silver">
        <p className="text-2xl font-black tracking-tighter">{totalCredits} SMS</p>
        <div className="text-md-softGray text-center text-sm font-normal">
          <p className="m-0 p-0">{formatNumberByLanguage(quantity)}</p>
          <p className="m-0 p-0 lowercase">{t('productTypes.smsMarketing.credits')}</p>
        </div>
      </div>
    );
  };

  const formatSMSCategoryName = (categoryName: string | ProductFeaturesTypes) =>
    `${categoryName} ${t('productTypes.smsMarketing.credits')}`;

  const countryOnDefaultChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountryProvider(e.target.value);
  };

  const getSMSUSAAdvise = () =>
    productsCollection && (
      <div className="sms__body__description__advise mt-8 py-3 px-4 rounded-2xl">
        <p>{t('productTypes.smsMarketing.USAAdvise')}</p>
        <Button
          className="sms__body__description__advise__button mt-4"
          size="free"
          type="button"
          variant="ghost"
          title={t('productTypes.emailMarketing.title')}
          onClick={() => onRequestInfo(productsCollection, 'US_PR_EC')}
        >
          {t('productTypes.smsMarketing.USAAdviseButtonLabel')}
        </Button>
      </div>
    );

  const getActionButtonLabel = (me: Me) => {
    if (userIsLogged && !me.capabilities.canBuyProducts) {
      return t('ProductDetail.writeUs');
    }

    return t('ProductDetail.payNow');
  };

  const getOnActionButton = (me: Me) => {
    if (userIsLogged && !me.capabilities.canBuyProducts) {
      return onRequestInfo;
    }

    return undefined;
  };

  return (
    <section className="bg-md-yellow">
      <div className="max-w-6xl m-auto px-3 py-2 lg:px-8 lg:pt-10 lg:pb-5">
        <h2 className="md__payments__title--h2">{title}</h2>
        <div className="flex justify-center lg:justify-between flex-wrap lg:flex-nowrap">
          <div
            className={clsx('mr-0 lg:mr-20 w-full relative', {
              'lg:w-11/12': !shouldShowTheCurrencySwitch(userProducts),
              'lg:w-7/12': shouldShowTheCurrencySwitch(userProducts),
            })}
          >
            <Slider
              min={min}
              max={max}
              step={step}
              onSliderChange={onDefaultSliderChange}
              defaultValue={defaultValue}
              marks={marksParsed}
              selectMarks={marks}
              selectClass="mb-5 lg:mb-0"
            />
            <p className="-ml-1 absolute mr-3">
              {t('productTypes.smsMarketing.creditsDisclaimer')}
            </p>
          </div>
          <div className="mr-0 mt-[75px] lg:mt-[-70px] flex justify-center lg:justify-end items-end flex-wrap z-[5]">
            <BillingModeUnique
              className={clsx({
                'mt-6 mr-0': !shouldShowTheCurrencySwitch(userProducts),
              })}
            />
            {shouldShowTheCurrencySwitch(userProducts) && (
              <div className="w-[150px] flex justify-center items-center flex-wrap">
                <CurrencySwitch
                  defaultValue={selectedProductCurrency}
                  onChange={onChangeCurrencySwitch}
                  className="ml-8"
                  title={t('common.currency.title')}
                />
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="flex flex-col-reverse lg:flex-row flex-wrap lg:flex-nowrap mt-2 lg:mt-12 font-black pb-10 gap-24 pt-2">
            <div className="w-full lg:w-2/3 gap-6">
              <SMSBodyWrap>
                <div className="text-center lg:text-left sms__body__description relative mt-0 lg:mt-5 text-md-blue font-normal">
                  <p className="text-base lg:text-xl">
                    {t('productTypes.smsMarketing.productDescription')}
                    <strong>{t('productTypes.smsMarketing.productDescription2')}</strong>
                    {t('productTypes.smsMarketing.productDescription3')}
                  </p>
                  <ol className="list-decimal text-base lg:text-xl mt-7 mx-10 mb-14">
                    <li>{t('productTypes.smsMarketing.productBullet1')}</li>
                    <li className="font-black">{t('productTypes.smsMarketing.productBullet2')}</li>
                    <li>{t('productTypes.smsMarketing.productBullet3')}</li>
                  </ol>
                  <p className="text-base lg:text-xl mt-5">
                    {t('productTypes.smsMarketing.productDescription4')}
                  </p>
                  <p className="text-base lg:text-xl font-black mt-5">
                    {t('productTypes.smsMarketing.productDescription5')}
                  </p>
                  {getSMSUSAAdvise()}
                  <p className="text-sm font-black mt-10 text-md-black italic">
                    {t('productTypes.smsMarketing.productDescription6')}
                  </p>
                </div>
              </SMSBodyWrap>
            </div>
            <div className="w-full lg:w-1/3">
              {renderSelect(null, 'countryForSMSSteps', {
                customClasses: 'w-full mt-[5px]',
                itemClasses: 'bg-md-white',
                options: countries,
                renderLabel: (item: Country) => renderLabel(i18n.language, item),
                value: getSMSCountryName(),
                onChange: countryOnDefaultChange,
              })}
              <ProductCard
                actionButtonLabel={getActionButtonLabel(me)}
                formatCategoryName={formatSMSCategoryName}
                formatProductName={formatSMSProductName}
                formatCredits={formatCredits}
                onActionButton={getOnActionButton(me)}
                onProductSelect={onProductSelect}
                onRequestInfo={onRequestInfo}
                product={productsCollection}
                userCurrency={selectedProductCurrency}
                userBillingMode={billingMode}
                className="w-full pt-0 px-1 pb-1"
              />
            </div>
          </div>
          {productsCollection && !productsCollection?.hidePrice && (
            <p className="text-right italic text-xs mt-7">{t('ProductDetail.taxesDisclaimer')}</p>
          )}
        </div>
      </div>
    </section>
  );
};
